import React, { useMemo, useState } from "react";
import {
  Box, Button, Grid, TextField, Typography, Paper, IconButton, CircularProgress,
  Divider, Avatar, Chip,
} from "@mui/material";
import { useFormik } from "formik";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StorefrontIcon from "@mui/icons-material/Storefront";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import NotesIcon from "@mui/icons-material/Notes";
import { useCreateVendorMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useNavigate } from "react-router";
import { uploadImage } from "../../../Components/UploadFile/upload";

const Index = () => {
  const navigate = useNavigate();
  const [newTax, { isLoading }] = useCreateVendorMutation();
  const { showErrorMessage, showMessage } = useNotifier();
  const [selectedImage, setSelectedImage] = useState<File | undefined>(undefined);

  const formik = useFormik({
    initialValues: {
      customerCompanyName: "", customerCompanyPhoneNumebr: "", customerCompanyEmail: "",
      customerFirstName: "", customerLastName: "", customerMobileNum: "", customerEmail: "",
      customerProvince: "", customerCity: "", customerPostalCode: "",
      customerStreetNumber: "", customerStreetName: "", customerDescription: "", customerImg: "",
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.customerCompanyName.trim()) errors.customerCompanyName = "Supplier name is required";
      if (values.customerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.customerEmail)) errors.customerEmail = "Invalid email";
      if (values.customerCompanyEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.customerCompanyEmail)) errors.customerCompanyEmail = "Invalid email";
      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        let customerImg: string | undefined;
        if (selectedImage) customerImg = (await uploadImage(selectedImage)) || "";
        const res = await newTax({ ...values, customerImg }).unwrap();
        if (!res.status) showErrorMessage(res.message || "Failed to create supplier");
        else { showMessage("Supplier created successfully"); navigate("/vendor/list"); resetForm(); setSelectedImage(undefined); }
      } catch { showErrorMessage("Something went wrong"); }
    },
  });

  const formValid = useMemo(() => formik.values.customerCompanyName.trim() !== "", [formik.values.customerCompanyName]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/jpg", "image/gif"].includes(file.type)) { showErrorMessage("Must be an image"); return; }
    if (file.size > 2048 * 1024) { showErrorMessage("Must be under 2 MB"); return; }
    setSelectedImage(file);
  };

  const Field = ({ label, required, ...props }: { label: string; required?: boolean; [k: string]: any }) => (
    <Box>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#334155", mb: 0.75, display: "flex", alignItems: "center", gap: 0.5 }}>
        {label} {required && <Typography component="span" sx={{ color: "#ef4444", fontSize: 11 }}>*</Typography>}
      </Typography>
      <TextField fullWidth size="small" {...props}
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, bgcolor: "#fff", "& fieldset": { borderColor: "#e2e8f0" }, "&:hover fieldset": { borderColor: "#cbd5e1" }, "&.Mui-focused fieldset": { borderColor: "#00838f", borderWidth: 1.5 } }, ...props.sx }} />
    </Box>
  );

  return (
    <Box sx={{ bgcolor: "#f8f9fb", minHeight: "100vh" }}>
      {/* ── Top Bar ── */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e2e8f0", px: 3, py: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, maxWidth: 1440, mx: "auto" }}>
          <IconButton onClick={() => navigate("/vendor/list")} size="small" sx={{ border: "1px solid #e2e8f0", borderRadius: 2, width: 36, height: 36 }}>
            <ArrowBackIcon sx={{ fontSize: 18, color: "#64748b" }} />
          </IconButton>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ flex: 1 }}>
            <Typography fontSize={18} fontWeight={700} color="#0f172a" letterSpacing="-0.02em">Create New Supplier</Typography>
          </Box>
          <Button variant="outlined" size="small" onClick={() => { formik.resetForm(); setSelectedImage(undefined); navigate("/vendor/list"); }}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#e2e8f0", color: "#64748b", mr: 1 }}>
            Cancel
          </Button>
          <Button variant="contained" disableElevation size="small" type="submit" disabled={!formValid || isLoading}
            onClick={() => formik.handleSubmit()}
            startIcon={isLoading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 700, bgcolor: "#0f172a", px: 3,
              "&:hover": { bgcolor: "#1e293b" }, "&.Mui-disabled": { bgcolor: "#94a3b8", color: "#fff" } }}>
            {isLoading ? "Saving..." : "Create Supplier"}
          </Button>
        </Box>
      </Box>

      {/* ── Content ── */}
      <Box sx={{ mx: "auto", p: 3 }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>

            {/* ════ LEFT COLUMN ════ */}
            <Grid item xs={12} lg={12}>

              {/* Supplier Name */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#ef4444" }} />
                  <Typography fontSize={14} fontWeight={700} color="#0f172a">Supplier Name</Typography>
                  <Chip label="Required" size="small" sx={{ height: 20, fontSize: 9, fontWeight: 700, bgcolor: "#fef2f2", color: "#dc2626", ml: 0.5 }} />
                </Box>
                <TextField fullWidth placeholder="Enter supplier or company name"
                  {...formik.getFieldProps("customerCompanyName")}
                  error={formik.touched.customerCompanyName && Boolean(formik.errors.customerCompanyName)}
                  helperText={formik.touched.customerCompanyName && formik.errors.customerCompanyName}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 15, fontWeight: 500, bgcolor: "#fff",
                    "& fieldset": { borderColor: "#e2e8f0" },
                    "&.Mui-focused fieldset": { borderColor: "#00838f", borderWidth: 2 } } }} />
              </Paper>

              {/* Company Information */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                  <BusinessIcon sx={{ fontSize: 18, color: "#3b82f6" }} />
                  <Typography fontSize={14} fontWeight={700} color="#0f172a">Company Information</Typography>
                </Box>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <Field label="Company Phone" placeholder="e.g. +1 (555) 000-0000" {...formik.getFieldProps("customerCompanyPhoneNumebr")} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field label="Company Email" placeholder="company@example.com" type="email"
                      {...formik.getFieldProps("customerCompanyEmail")}
                      error={formik.touched.customerCompanyEmail && Boolean(formik.errors.customerCompanyEmail)}
                      helperText={formik.touched.customerCompanyEmail && formik.errors.customerCompanyEmail} />
                  </Grid>
                </Grid>
              </Paper>

              {/* Contact Person */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                  <PersonIcon sx={{ fontSize: 18, color: "#16a34a" }} />
                  <Typography fontSize={14} fontWeight={700} color="#0f172a">Contact Person</Typography>
                </Box>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <Field label="First Name" placeholder="John" {...formik.getFieldProps("customerFirstName")} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field label="Last Name" placeholder="Doe" {...formik.getFieldProps("customerLastName")} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field label="Mobile Number" placeholder="e.g. +1 (555) 000-0000" {...formik.getFieldProps("customerMobileNum")} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field label="Email" placeholder="john@example.com" type="email"
                      {...formik.getFieldProps("customerEmail")}
                      error={formik.touched.customerEmail && Boolean(formik.errors.customerEmail)}
                      helperText={formik.touched.customerEmail && formik.errors.customerEmail} />
                  </Grid>
                </Grid>
              </Paper>

              {/* Address */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                  <LocationOnIcon sx={{ fontSize: 18, color: "#8b5cf6" }} />
                  <Typography fontSize={14} fontWeight={700} color="#0f172a">Address</Typography>
                </Box>
                <Grid container spacing={2.5}>
                  <Grid item xs={4} sm={2}>
                    <Field label="Street #" placeholder="#" {...formik.getFieldProps("customerStreetNumber")} />
                  </Grid>
                  <Grid item xs={8} sm={4}>
                    <Field label="Street Name" placeholder="Main St" {...formik.getFieldProps("customerStreetName")} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field label="City" placeholder="City" {...formik.getFieldProps("customerCity")} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field label="Province / State" placeholder="Province" {...formik.getFieldProps("customerProvince")} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field label="Postal Code" placeholder="A1B 2C3" {...formik.getFieldProps("customerPostalCode")} />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* ════ RIGHT COLUMN ════ */}
            <Grid item xs={12} lg={12}>

              {/* Image */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                <Typography fontSize={14} fontWeight={700} color="#0f172a" mb={2.5}>Supplier Logo</Typography>
                <Box sx={{
                  width: "100%", aspectRatio: "16/10", borderRadius: 2, bgcolor: "#f8fafc",
                  border: selectedImage ? "none" : "2px dashed #e2e8f0",
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  overflow: "hidden", position: "relative", mb: 2, cursor: "pointer",
                  transition: "all 0.2s", "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f1f5f9" },
                }}>
                  {selectedImage ? (
                    <>
                      <img src={URL.createObjectURL(selectedImage)} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                      <IconButton size="small" onClick={() => setSelectedImage(undefined)}
                        sx={{ position: "absolute", top: 8, right: 8, bgcolor: "rgba(0,0,0,0.5)", color: "#fff", width: 28, height: 28, "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}>
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </>
                  ) : (
                    <label htmlFor="supplier-img" style={{ cursor: "pointer", textAlign: "center", width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                      <CloudUploadIcon sx={{ fontSize: 32, color: "#cbd5e1", mb: 1 }} />
                      <Typography fontSize={12} color="#94a3b8" fontWeight={500}>Click to upload</Typography>
                      <Typography fontSize={10} color="#cbd5e1">JPG, PNG, GIF up to 2MB</Typography>
                    </label>
                  )}
                </Box>
                <input accept="image/*" style={{ display: "none" }} id="supplier-img" type="file" onChange={handleImageChange} />
                {!selectedImage && (
                  <label htmlFor="supplier-img">
                    <Button component="span" fullWidth variant="outlined" size="small" startIcon={<CloudUploadIcon sx={{ fontSize: 14 }} />}
                      sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#e2e8f0", color: "#64748b" }}>
                      Upload Image
                    </Button>
                  </label>
                )}
              </Paper>

              {/* Notes */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                  <NotesIcon sx={{ fontSize: 18, color: "#f59e0b" }} />
                  <Typography fontSize={14} fontWeight={700} color="#0f172a">Notes</Typography>
                </Box>
                <TextField fullWidth multiline rows={4} size="small" placeholder="Payment terms, delivery preferences, special instructions..."
                  {...formik.getFieldProps("customerDescription")}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, bgcolor: "#fff", "& fieldset": { borderColor: "#e2e8f0" } } }} />
              </Paper>

              {/* Save Actions */}
              <Paper elevation={0} sx={{ p: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                <Button fullWidth variant="contained" disableElevation disabled={!formValid || isLoading}
                  onClick={() => formik.handleSubmit()}
                  startIcon={isLoading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 18 }} />}
                  sx={{ borderRadius: 2, py: 1.5, textTransform: "none", fontWeight: 700, fontSize: 14,
                    bgcolor: "#0f172a", "&:hover": { bgcolor: "#1e293b", boxShadow: "0 4px 14px rgba(15,23,42,0.25)" },
                    "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" } }}>
                  {isLoading ? "Saving..." : "Create Supplier"}
                </Button>
                <Button fullWidth variant="text" size="small"
                  onClick={() => { formik.resetForm(); setSelectedImage(undefined); navigate("/vendor/list"); }}
                  sx={{ mt: 1, borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, color: "#94a3b8" }}>
                  Cancel
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Box>
  );
};

export default Index;
